// ============================================================================
// useSearchAgent — hoisted search-agent store
//
// Owns the search-agent flow (searchAgent mutation + onAgentResponse
// subscription) and all of its result state. This hook is intended to be
// mounted ABOVE SearchPopup (e.g. in Home.js) so the search state survives
// closing/reopening the popup — mirroring how AgentChatProvider keeps chat
// state alive across popup unmounts.
//
// Previously this logic lived inside SearchPopup as a mount `useEffect`,
// which meant closing the popup (unmounting it) destroyed the results and
// reopening re-fired the search. By hoisting it here and triggering searches
// from an explicit `runSearch(query)` action, reopening the popup simply
// re-renders the preserved results with no network refire.
// ============================================================================

import { useState, useRef, useEffect, useCallback } from 'react';
import { searchAgent, onAgentResponse } from 'src/utilities/AgentChatAPIs';
import { createDebugLog } from 'src/Ateam-BI/AutoMate/utils/aiDebugLog';
import useQuickSearch from 'src/Ateam-BI/AutoMate/hooks/useQuickSearch';
import { SEARCH_AGENT_QUICK_SEARCH_ENABLED, SEARCH_AGENT_MAX_RESULTS } from 'src/Ateam-BI/AutoMate/constants';
import { usePersonaContext } from 'src/Ateam-BI/AutoMate/context/PersonaContext';

const debugLog = createDebugLog('[useSearchAgent]');

/**
 * Hoisted search-agent store.
 *
 * @returns {{
 *   query: string,            // the query string of the most recent search
 *   searchAnswer: string,     // assembled streamed prose answer
 *   searchSources: object[],  // structured sources from the COMPLETE chunk
 *   quickSearchResults: object[], // quick-search prefetch cards
 *   isSearching: boolean,     // true while a search is in flight
 *   searchError: (string|null),
 *   searchSessionId: (string|null),      // sessionId from the AgentMessageAck
 *   searchConversationId: (string|null), // conversationId from the AgentMessageAck
 *   runSearch: (query: string) => void, // fire a new search
 * }}
 */
const useSearchAgent = () => {
  const { executeQuickSearch } = useQuickSearch();
  const { effectivePersona, papiJobData, alias } = usePersonaContext();

  const [query, setQuery] = useState('');
  const [searchAnswer, setSearchAnswer] = useState('');
  const [searchSources, setSearchSources] = useState([]);
  const [quickSearchResults, setQuickSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  // The (sessionId, conversationId) pair from the AgentMessageAck. Surfaced so
  // SearchPopup's FeedbackButtons can attach feedback to the correct turn.
  const [searchSessionId, setSearchSessionId] = useState(null);
  const [searchConversationId, setSearchConversationId] = useState(null);

  // Sparse buffer indexed by `chunkIndex` so out-of-order delivery over the
  // subscription WebSocket still assembles correctly.
  const chunksRef = useRef([]);
  const requestIdRef = useRef(0);
  const subscriptionRef = useRef(null);
  const searchCompletedRef = useRef(false);
  const mountedRef = useRef(true);

  // Keep persona inputs in a ref so `runSearch` can stay referentially stable
  // (useCallback with empty deps) while still reading the latest persona data.
  const personaRef = useRef({ effectivePersona, papiJobData, alias });
  useEffect(() => {
    personaRef.current = { effectivePersona, papiJobData, alias };
  }, [effectivePersona, papiJobData, alias]);

  // Tear down any live subscription when the owner (Home.js) unmounts.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (subscriptionRef.current && typeof subscriptionRef.current.unsubscribe === 'function') {
        subscriptionRef.current.unsubscribe();
      }
      subscriptionRef.current = null;
    };
  }, []);

  const runSearch = useCallback((rawQuery) => {
    const trimmed = (rawQuery ?? '').trim();
    if (!trimmed) return;

    const currentRequestId = ++requestIdRef.current;
    debugLog('new search', { query: trimmed, requestId: currentRequestId });

    // Tear down any prior subscription before starting a new one.
    if (subscriptionRef.current && typeof subscriptionRef.current.unsubscribe === 'function') {
      subscriptionRef.current.unsubscribe();
    }
    subscriptionRef.current = null;

    // Reset state for the new search.
    chunksRef.current = [];
    setQuery(trimmed);
    setSearchAnswer('');
    setSearchSources([]);
    setQuickSearchResults([]);
    setSearchError(null);
    setIsSearching(true);
    setSearchSessionId(null);
    setSearchConversationId(null);
    searchCompletedRef.current = false;

    // Joins every chunk we currently have, in index order, skipping gaps.
    const joinContent = () => chunksRef.current.filter((c) => c != null).join('');

    const fireSearchAgent = () => {
      const { effectivePersona: ep, papiJobData: pj, alias: al } = personaRef.current;
      // Requirement 6.2: include PersonaInput with every search mutation.
      const personaInput = {
        personaClassification: ep,
        alias: al || '',
        jobFamilyName: pj?.jobFamilyName || '',
        jobProfileName: pj?.jobProfileName || '',
        jobLevel: pj?.jobLevel || '',
      };

      searchAgent(
        trimmed,
        SEARCH_AGENT_MAX_RESULTS,
        null,
        (result) => {
          // Staleness guard — drop responses from superseded requests.
          if (currentRequestId !== requestIdRef.current) return;

          const { conversationId } = result?.data?.searchAgent || {};
          if (!conversationId) {
            if (!mountedRef.current) return;
            debugLog('searchAgent returned no conversationId');
            setIsSearching(false);
            setSearchError('Search did not return a conversation id. Please try again.');
            return;
          }
          debugLog('searchAgent ack', { conversationId });

          // Capture the (sessionId, conversationId) pair so SearchPopup's
          // FeedbackButtons can attach feedback to this search invocation.
          const ackSessionId = result?.data?.searchAgent?.sessionId;
          setSearchSessionId(ackSessionId || null);
          setSearchConversationId(conversationId);

          onAgentResponse(
            conversationId,
            (response) => {
              if (currentRequestId !== requestIdRef.current) return;
              const chunk = response?.value?.data?.onAgentResponse;
              if (!chunk) return;

              if (chunk.status === 'STREAMING') {
                debugLog('STREAMING chunk', {
                  chunkIndex: chunk.chunkIndex,
                  contentLength: chunk.content?.length ?? 0,
                });
                const idx = typeof chunk.chunkIndex === 'number'
                  ? chunk.chunkIndex
                  : chunksRef.current.length;
                chunksRef.current[idx] = chunk.content ?? '';
                if (!mountedRef.current) return;
                setSearchAnswer(joinContent());
              } else if (chunk.status === 'COMPLETE') {
                debugLog('COMPLETE chunk', {
                  correlationId: chunk.correlationId,
                  sourceCount: Array.isArray(chunk.sources) ? chunk.sources.length : 0,
                });
                // COMPLETE may also carry trailing prose content; fold it in.
                if (chunk.content) {
                  const idx = typeof chunk.chunkIndex === 'number'
                    ? chunk.chunkIndex
                    : chunksRef.current.length;
                  chunksRef.current[idx] = chunk.content;
                }
                if (!mountedRef.current) return;
                setSearchAnswer(joinContent());
                if (Array.isArray(chunk.sources)) {
                  setSearchSources(chunk.sources);
                }
                setIsSearching(false);
                searchCompletedRef.current = true;
              } else if (chunk.status === 'ERROR') {
                debugLog('ERROR chunk', { content: chunk.content });
                if (!mountedRef.current) return;
                setSearchError(chunk.content || 'Search failed. Please try again.');
                setIsSearching(false);
                searchCompletedRef.current = true;
              }
            },
            (error) => {
              if (currentRequestId !== requestIdRef.current) return;
              // eslint-disable-next-line no-console
              console.error('[useSearchAgent] subscription error:', error);
              if (!mountedRef.current) return;
              // Suppress spurious post-completion close errors (keep-alive timeout).
              if (searchCompletedRef.current) return;
              setSearchError('Search connection failed. Please try again.');
              setIsSearching(false);
            }
          ).then((sub) => {
            // If a newer search started since we fired the subscribe(), the
            // sub belongs to an abandoned request — tear it down.
            if (!mountedRef.current || currentRequestId !== requestIdRef.current) {
              if (sub && typeof sub.unsubscribe === 'function') sub.unsubscribe();
              return;
            }
            subscriptionRef.current = sub;
          });
        },
        (error) => {
          if (currentRequestId !== requestIdRef.current) return;
          // eslint-disable-next-line no-console
          console.error('[useSearchAgent] searchAgent error:', error);
          if (!mountedRef.current) return;
          setSearchError('Search failed. Please try again.');
          setIsSearching(false);
        },
        // Options bag: pass persona so the backend can tailor search responses.
        { persona: personaInput }
      );
    };

    // Fire quick search and the search agent CONCURRENTLY (no data dependency
    // between them). The search agent is no longer gated on quick search
    // settling, so the streamed answer starts as soon as possible instead of
    // waiting out the quick-search round-trip/timeout.
    //
    // Render order is independent of arrival order: SearchPopup renders the
    // quick-search cards in a fixed slot ABOVE the agent answer, so whichever
    // resolves first simply fills its own slot — the cards always appear above
    // the answer. Staleness guards (requestId/mountedRef) keep both paths safe
    // when concurrent.
    if (SEARCH_AGENT_QUICK_SEARCH_ENABLED) {
      executeQuickSearch(trimmed, 4)
        .then((quickResult) => {
          if (currentRequestId !== requestIdRef.current) return;
          if (!mountedRef.current) return;
          const results = quickResult?.results ?? [];
          setQuickSearchResults(results);
        })
        .catch((err) => {
          // Quick search failure is non-fatal — log and ignore. The search
          // agent runs independently, so a quick-search failure no longer
          // affects whether/when the agent fires or how loading clears.
          // eslint-disable-next-line no-console
          console.error('[useSearchAgent] quick search failed:', err);
        });
    }

    // Always fire the search agent immediately, in parallel with (and
    // independent of) quick search. This owns `isSearching` clearing via its
    // own success/error/COMPLETE paths.
    fireSearchAgent();
  }, [executeQuickSearch]);

  return {
    query,
    searchAnswer,
    searchSources,
    quickSearchResults,
    isSearching,
    searchError,
    searchSessionId,
    searchConversationId,
    runSearch,
  };
};

export default useSearchAgent;
